import {createSelector} from '@reduxjs/toolkit';
import {
  getAllParkingLots,
  getFilteredAndSortedParkingLots,
  getParkingLotsByBuildingId,
} from '../../data/parkingLot/selectors';
import {getAreAllParkingNodesFull, getParkingInformationByNodeId} from '../parkingInformation/selectors';
import {RootState} from '../../rootReducer';
import {getAreAllParkingNodesClosed, getDefaultBuildingId} from '../../data';
import {getIsParkingFeatureEnabled} from '../../infrastructure';
import {
  getParkingQuotaProfile,
  getParkingLocationProfile,
  getIsSelectedDateFuture,
  getIsDayAllowed,
} from '../../data/profiles/selectors';
import {getParkingWeekReservationsPerDate} from '../../data/parkingReservation/selectors';
import createCachedSelector from 're-reselect';
import {ParkingNode} from './types';
import {sortBy} from 'lodash';
import {parseISO} from 'date-fns';

const getAllParkingNodes = createCachedSelector(
  getFilteredAndSortedParkingLots,
  (filteredAndSortedParkingLots): ParkingNode[] => {
    const parkingNodes = filteredAndSortedParkingLots.flatMap((parkingLot) => {
      const {parkingZones} = parkingLot;
      if (!parkingZones.length) {
        return <ParkingNode>{
          parkingZoneId: null,
          subtitle: null,
          parkingLotId: parkingLot.id,
          title: parkingLot.name,
          nodeId: parkingLot.id,
        };
      }
      return parkingZones
        .filter((zone) => zone && zone.capacity > 0)
        .map(
          (zone) =>
            <ParkingNode>{
              parkingZoneId: zone.id,
              subtitle: parkingLot.name,
              parkingLotId: zone.parentId,
              title: zone.name,
              nodeId: zone.id,
            },
        );
    });

    return sortBy(parkingNodes, (node) => node.title);
  },
)((_, buildingId) => buildingId);

export const getParkingNodesWithAvailabilityGroup = createCachedSelector(
  (state: RootState, date: string, buildingId: string) => getAllParkingNodes(state, buildingId),
  (state: RootState, date: string, buildingId: string) => ({
    state,
    date,
    buildingId,
  }),
  (parkingNodes, {state, date, buildingId}) => {
    const parsedDate = parseISO(date);
    return parkingNodes.map((parkingNode) => {
      const {isFull, isClosed, ...restParkingNodeInfoProps} = getParkingInformationByNodeId(state, {
        buildingId,
        date: parsedDate,
        nodeId: parkingNode.nodeId,
      });

      return {
        ...parkingNode,
        nodeAvailability: isFull || isClosed ? 'Unavailable' : 'Available',
        isClosed,
        ...restParkingNodeInfoProps,
      };
    });
  },
)((_state, date, buildingId) => `${date}-${buildingId}`);

export const getIsParkingFeatureVisible = createSelector(
  getParkingQuotaProfile,
  getIsParkingFeatureEnabled,
  getParkingLocationProfile,
  getAllParkingLots,
  (parkingQuotaProfile, parkingFeatureEnabled, parkingLocationProfile, parkingLots) => {
    return (
      parkingFeatureEnabled &&
      parkingQuotaProfile.daysPerWeek > 0 &&
      parkingLots.length > 0 &&
      (parkingLocationProfile.allowedLocationIds.length > 0 || parkingLocationProfile.allowAll)
    );
  },
);

export const getIsParkingFeatureVisibleForBuilding = createSelector(
  getParkingQuotaProfile,
  getIsParkingFeatureEnabled,
  getParkingLocationProfile,
  getFilteredAndSortedParkingLots,
  (parkingQuotaProfile, parkingFeatureEnabled, parkingLocationProfile, parkingLots) => {
    return (
      parkingFeatureEnabled &&
      parkingQuotaProfile.daysPerWeek > 0 &&
      parkingLots.length > 0 &&
      (parkingLocationProfile.allowedLocationIds.length > 0 || parkingLocationProfile.allowAll)
    );
  },
);

export const getIsParkingFeatureVisibleForDefaultBuilding = (state: RootState) =>
  getIsParkingFeatureVisibleForBuilding(state, getDefaultBuildingId(state) ?? '');

export const getParkingQuotaHasBeenReached = createSelector(
  getParkingWeekReservationsPerDate,
  ({bookingCount, maxBookings}) => {
    return bookingCount >= maxBookings;
  },
);

export const getIsUserAllowedToReserveParking = createSelector(
  getAreAllParkingNodesClosed,
  getAreAllParkingNodesFull,
  (state: RootState, date: Date | undefined, buildingId: string) => getParkingLotsByBuildingId(state, buildingId),
  (state: RootState, _date: Date | undefined, buildingId: string) =>
    getIsParkingFeatureVisibleForBuilding(state, buildingId),
  getParkingQuotaHasBeenReached,
  getIsSelectedDateFuture,
  getIsDayAllowed,
  (
    areAllParkingsClosed,
    areAllParkingsFull,
    parkingLots,
    isParkingReservationAllowed,
    quotaHasBeenReached,
    isSelectedDateFuture,
    isDayAllowed,
  ) =>
    !quotaHasBeenReached &&
    isParkingReservationAllowed &&
    !areAllParkingsClosed &&
    !areAllParkingsFull &&
    !!parkingLots?.length &&
    !isSelectedDateFuture &&
    isDayAllowed,
);
